import { createContext, useContext, useEffect, useState } from 'react';

import styled from 'styled-components';

import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { Form, useCreateFormStore } from '@components/form';
import { FormPasswordFieldProps } from '@components/form/form-password-field';
import { IconLock } from '@components/icons/lock';
import { Select } from '@components/select';
import { Sidebar, hideSidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Switch } from '@components/switch';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { useToaster } from '@components/toaster';
import { useScopedTranslate } from '@locale/translate';

import { useAuthenticationTest, useUpdateIntegrationAuthentication } from 'api/integrations/hooks';
import {
  ApiKeyAuthentication,
  Authentication,
  AuthorizationHeaderAuthentication,
  BasicAuthentication,
  BearerTokenAuthentication,
  Integration,
  JwtTokenAuthentication,
  OAuth2Authentication,
} from 'api/integrations/requests';

export type IntegrationProps = {
  integration: Integration;
};

const AuthTypeLabel: Record<Authentication['authType'], keyof (typeof locale)['en']> = {
  '': 'none',
  basic: 'basic',
  bearer_token: 'bearerToken',
  oauth2: 'oauth2',
  api_key: 'apiKey',
  authorization_header: 'authorizationHeader',
  jwt_token: 'jwtToken',
};

export const useAuthTypeLabel = (authType: Authentication['authType'] = '') => {
  const { t } = useScopedTranslate(locale);

  return t(AuthTypeLabel[authType]);
};

export const AuthenticationStatus = ({ integration }: IntegrationProps) => {
  const { t } = useScopedTranslate(locale);
  const isAuthenticated = integration?.authentication?.isAuthenticated;

  return isAuthenticated ? (
    <TextStatusIndicator text={t('connected')} variant="success" />
  ) : (
    <TextStatusIndicator text={t('notConnected')} variant="pending" />
  );
};

export const useAuthenticationSidebar = () =>
  useSidebar(AuthenticationSidebar, { variant: 'REGULAR' });

type AuthenticationProps<T = Authentication> = {
  authentication: T;
};

const ExtraHeadersField = () => {
  const { t } = useScopedTranslate(locale);
  return (
    <Form.Textarea
      id="extraHeaders"
      label={t('extraHeaders')}
      rows={2}
      validators={['json']}
      placeholder={t('secretUpdatePlaceholder')}
    />
  );
};

const PasswordField = (props: Pick<FormPasswordFieldProps<any>, 'label' | 'id'>) => {
  const { t } = useScopedTranslate(locale);
  const isEdit = useContext(IsEditContext);
  const placeholder = isEdit ? t('secretUpdatePlaceholder') : '';
  return (
    <Form.PasswordField
      autoComplete="new-password"
      placeholder={placeholder}
      hideVisibilityOnEmpty
      {...props}
    />
  );
};

const Advanced: React.FC = ({ children }) => {
  const { t } = useScopedTranslate(locale);
  const [isAdvanced, setIsAdvanced] = useState(false);

  return (
    <>
      <Switch label={t('toggleAdvanced')} onChange={() => setIsAdvanced(!isAdvanced)} />
      {isAdvanced && (
        <>
          {children}
          <ExtraHeadersField />
        </>
      )}
    </>
  );
};

const AuthorizationHeaderForm = ({
  authentication,
}: AuthenticationProps<AuthorizationHeaderAuthentication>) => {
  const { t } = useScopedTranslate(locale);

  return (
    <>
      <Form.TextField
        id="prefix"
        name="prefix"
        label={t('prefix')}
        defaultValue={authentication.details.prefix}
      />
      <PasswordField id="token" label={t('token')} />
      <Advanced />
    </>
  );
};

const BearerTokenForm = ({ authentication }: AuthenticationProps<BearerTokenAuthentication>) => {
  const { t } = useScopedTranslate(locale);

  return (
    <>
      <Form.TextField id="url" label={t('authUrl')} defaultValue={authentication.details.url} />
      <Form.TextField
        id="clientId"
        label={t('clientId')}
        defaultValue={authentication.details.clientId}
      />
      <PasswordField id="clientSecret" label={t('clientSecret')} />
      <Advanced>
        <Form.Select
          id="requestType"
          label={t('requestType')}
          options={[
            { label: 'JSON', value: 'json' },
            { label: 'Multipart', value: 'multipart' },
          ]}
          defaultValue={authentication.details.requestType ?? 'json'}
        />
        <Form.TextField
          id="clientIdKey"
          label={t('clientIdKey')}
          defaultValue={authentication.details.clientIdKey || 'client_id'}
        />
        <Form.TextField
          id="clientSecretKey"
          label={t('clientSecretKey')}
          defaultValue={authentication.details.clientSecretKey || 'client_secret'}
        />
        <Form.Textarea id="extraData" label={t('extraData')} rows={5} validators={['json']} />
        <Form.TextField
          id="prefix"
          label={t('prefix')}
          defaultValue={authentication.details.prefix ?? 'Bearer'}
        />
        <Form.TextField
          id="tokenLocation"
          label={t('tokenLocation')}
          defaultValue={authentication.details.tokenLocation}
        />
      </Advanced>
    </>
  );
};

const BasicForm = ({ authentication }: AuthenticationProps<BasicAuthentication>) => {
  const { t } = useScopedTranslate(locale);

  return (
    <>
      <Form.TextField
        id="username"
        label={t('username')}
        defaultValue={authentication.details.username}
        validators={['required']}
      />
      <PasswordField id="password" label={t('password')} />
      <Advanced />
    </>
  );
};

const Oauth2Form = ({ authentication }: AuthenticationProps<OAuth2Authentication>) => {
  const { t } = useScopedTranslate(locale);

  return (
    <>
      <Form.TextField
        id="tokenUrl"
        label={t('tokenUrl')}
        defaultValue={authentication.details.tokenUrl}
        validators={['required']}
      />
      <Form.TextField
        id="grantType"
        label={t('grantType')}
        defaultValue={authentication.details.grantType ?? 'authorization_code'}
      />
      <Form.TextField
        id="clientId"
        label={t('clientId')}
        defaultValue={authentication.details.clientId}
        validators={['required']}
      />
      <PasswordField id="clientSecret" label={t('clientSecret')} />
      <Advanced />
    </>
  );
};

const ApiKeyForm = ({ authentication }: AuthenticationProps<ApiKeyAuthentication>) => {
  const { t } = useScopedTranslate(locale);

  return (
    <>
      <Form.TextField
        id="headerKey"
        label={t('headerKey')}
        defaultValue={authentication.details.headerKey}
      />
      <PasswordField id="apiKey" label={t('apiKey')} />
      <Advanced />
    </>
  );
};

const JwtTokenForm = ({ authentication }: AuthenticationProps<JwtTokenAuthentication>) => {
  const { t } = useScopedTranslate(locale);

  return (
    <>
      <Form.TextField
        id="issuer"
        label={t('issuer')}
        defaultValue={authentication.details.issuer}
        validators={['required']}
      />
      <Form.TextField
        id="key"
        label={t('jwtKey')}
        defaultValue={authentication.details.key}
        validators={['required']}
      />
      <PasswordField id="secret" label={t('secret')} />
      <Advanced>
        <Form.TextField
          id="subject"
          label={t('subject')}
          defaultValue={authentication.details.subject}
        />
        <Form.TextField
          id="expirationSeconds"
          label={t('expirationSeconds')}
          defaultValue={authentication.details.expirationSeconds?.toString() ?? '30'}
          validators={['number']}
        />
        <Form.TextField
          id="prefix"
          label={t('prefix')}
          defaultValue={authentication.details.prefix ?? 'Bearer'}
        />
      </Advanced>
    </>
  );
};

const AuthComponent = ({ authentication }: AuthenticationProps) => {
  switch (authentication.authType) {
    case 'authorization_header':
      return <AuthorizationHeaderForm authentication={authentication} />;
    case 'bearer_token':
      return <BearerTokenForm authentication={authentication} />;
    case 'basic':
      return <BasicForm authentication={authentication} />;
    case 'oauth2':
      return <Oauth2Form authentication={authentication} />;
    case 'api_key':
      return <ApiKeyForm authentication={authentication} />;
    case 'jwt_token':
      return <JwtTokenForm authentication={authentication} />;
    default:
      return null;
  }
};

const IsEditContext = createContext(false);

const AuthenticationSidebar = ({ integration }: { integration: Integration }) => {
  const { t } = useScopedTranslate(locale);
  const [authType, setAuthType] = useState(integration.authentication?.authType ?? '');
  const isEdit = Boolean(integration.authentication?.authType === authType);
  const updateAuthenticationMutation = useUpdateIntegrationAuthentication(integration.shortId);
  const testMutation = useAuthenticationTest();
  const { showSuccess } = useToaster();
  const formStore = useCreateFormStore({ values: integration.authentication?.details ?? {} });

  const getAuthentication = () =>
    ({ authType, details: formStore.getState().values } as Authentication);

  const handleSave = async () => {
    await updateAuthenticationMutation.mutateAsync(getAuthentication());
    showSuccess(t('authUpdated'));
    hideSidebar();
  };

  const testRequired = ['bearer_token', 'oauth2'].includes(authType);
  const [testStatus, setTestStatus] = useState('');

  const isError = testMutation.isError || (testMutation.isSuccess && !testMutation?.data?.success);
  const isSuccess = testMutation.isSuccess && testMutation?.data.success;

  useEffect(() => {
    setTestStatus(isSuccess ? 'success' : isError ? 'error' : '');
  }, [isError, isSuccess]);

  const canSave = !testRequired || testStatus === 'success';

  return (
    <Sidebar>
      <IsEditContext.Provider value={isEdit}>
        <Sidebar.SimpleHeader title={t('header')} />
        <Form onSubmit={handleSave} store={formStore}>
          <Sidebar.Content>
            <Stack space={4}>
              <Select
                label={t('type')}
                options={Object.entries(AuthTypeLabel).map(([value, translationKey]) => ({
                  label: t(translationKey),
                  value,
                }))}
                defaultValue={authType}
                onChange={(value) => {
                  setTestStatus('');
                  setAuthType(value as Authentication['authType']);
                }}
              />
              <AuthComponent authentication={getAuthentication()} />
              {authType && testRequired && (
                <>
                  <Stack orientation="horizontal">
                    <Button
                      text={t('testAuth')}
                      onClick={() => testMutation.mutateAsync(getAuthentication())}
                      iconLeft={<IconLock />}
                      isDisabled={testMutation.isLoading}
                    />
                  </Stack>
                  {testStatus === 'success' && (
                    <Alert variant="success">
                      <Text text={t('authTestSuccess')} />
                    </Alert>
                  )}
                  {testStatus === 'error' && (
                    <Alert variant="error">
                      <CodeBlock>
                        {JSON.stringify(testMutation.data, null, 2) || t('generalError')}
                      </CodeBlock>
                    </Alert>
                  )}
                </>
              )}
            </Stack>
          </Sidebar.Content>
          <Sidebar.Footer>
            <Stack space={2} orientation="horizontal" justify="flex-end">
              <Form.Actions>
                <Button text={t('core.cancel')} variant="secondary" onClick={hideSidebar} />
                <Form.Submit text={t('core.save')} variant="primary-blue" disabled={!canSave} />
              </Form.Actions>
            </Stack>
          </Sidebar.Footer>
        </Form>
      </IsEditContext.Provider>
    </Sidebar>
  );
};

const locale = {
  en: {
    header: 'Authentication',
    type: 'Authentication type',
    toggleAdvanced: 'Advanced settings',
    extraData: 'Extra data (optional)',
    extraHeaders: 'Extra headers (optional)',
    requestType: 'Request type',
    prefix: 'Prefix (optional)',
    tokenUrl: 'Token URL',
    grantType: 'Grant type',
    authUrl: 'Authentication URL',
    clientId: 'Client ID',
    clientIdKey: 'Client ID key',
    clientSecret: 'Client secret',
    clientSecretKey: 'Client secret key',
    tokenLocation: 'Token location (data.token for {data: {token: XYZ}})',
    username: 'Username',
    password: 'Password',
    apiKey: 'API key',
    headerKey: 'Header key',
    token: 'Token',
    testAuth: 'Test',
    authTestSuccess: 'Authentication test successful',
    authUpdated: 'Authentication updated',
    generalError: 'Authorization returned an unspecified error',
    none: 'None',
    basic: 'Basic',
    bearerToken: 'Bearer token',
    oauth2: 'OAuth2',
    authorizationHeader: 'Authorization header',
    jwtToken: 'JWT Token',
    issuer: 'Issuer (API User ID)',
    jwtKey: 'Key',
    secret: 'Secret',
    subject: 'Subject (optional)',
    expirationSeconds: 'Expiration seconds (default: 30)',
    connected: 'Connected',
    notConnected: 'Not connected',
    secretUpdatePlaceholder: 'Leave blank to keep current',
  },
};

const CodeBlock = styled.pre(() => ({
  'word-wrap': 'break-word',
  'white-space': 'pre-wrap',
}));
